import {
  ArrowDown,
  ArrowDownRight,
  ArrowUpRight,
  ChevronRight,
  Search,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { formatCompact, formatDuration, formatTime } from '@/lib/format';
import { cn } from '@/lib/utils';

/**
 * Building blocks of the session-style trace view: a header with chips and metrics, an event
 * timeline with zoom, a searchable transcript and a side panel with facts and a usage chart.
 */

export type TimelineKind = 'user' | 'agent' | 'model' | 'tool' | 'error' | 'step';

export interface TimelineEvent {
  id: string;
  kind: TimelineKind;
  label: string;
  /** Offsets from the session start. `endMs` is null while still running. */
  startMs: number;
  endMs: number | null;
  failed?: boolean;
}

const BLOCK: Record<TimelineKind, string> = {
  user: 'bg-tint-rose',
  agent: 'bg-tint-blue/85 hover:bg-tint-blue',
  model: 'bg-tint-violet/80 hover:bg-tint-violet',
  tool: 'bg-tint-amber/80 hover:bg-tint-amber',
  step: 'bg-muted-foreground/45 hover:bg-muted-foreground/60',
  error: 'bg-destructive/85 hover:bg-destructive',
};

const KIND_LABEL: Record<TimelineKind, string> = {
  user: 'User',
  agent: 'Agent',
  model: 'Model call',
  tool: 'Tool',
  step: 'Step',
  error: 'Error',
};

const LANE: Record<TimelineKind, 0 | 1> = {
  user: 0,
  agent: 0,
  model: 1,
  tool: 1,
  step: 1,
  error: 1,
};

const ZOOMS = [1, 2, 4, 8, 16];
const MAX_ROWS = 6;
const MAIN_H = 26;

/** Packs overlapping lane-1 events (parallel tool calls) into rows, first fit. */
function packRows(events: TimelineEvent[], total: number) {
  const rowOf = new Map<string, number>();
  const ends: number[] = [];
  const lane = events.filter((e) => LANE[e.kind] === 1).sort((a, b) => a.startMs - b.startMs);
  for (const ev of lane) {
    let row = ends.findIndex((end) => end <= ev.startMs);
    if (row === -1) row = ends.length < MAX_ROWS ? ends.length : ends.indexOf(Math.min(...ends));
    ends[row] = Math.max(ends[row] ?? 0, ev.endMs ?? total);
    rowOf.set(ev.id, row);
  }
  return { rowOf, rows: ends.length };
}

/** The session as one track of blocks on a clock axis; hover reads the time, click selects. */
export function EventTimeline({
  events,
  startAt,
  totalMs,
  selected,
  onSelect,
  toolbar,
  labels,
}: {
  events: TimelineEvent[];
  /** Epoch ms of offset 0. */
  startAt: number;
  totalMs: number;
  selected: string | null;
  onSelect: (id: string) => void;
  /** Shown left of the zoom controls (e.g. the transcript search). */
  toolbar?: ReactNode;
  /** Renames kinds in the legend and labels (e.g. the root lane of a workflow). */
  labels?: Partial<Record<TimelineKind, string>>;
}) {
  const kindLabel = (k: TimelineKind) => labels?.[k] ?? KIND_LABEL[k];
  const [zoom, setZoom] = useState(0);
  const [width, setWidth] = useState(800);
  const [hover, setHover] = useState<number | null>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const total = Math.max(totalMs, 1);
  const { rowOf, rows } = useMemo(() => packRows(events, total), [events, total]);
  const twoLanes = rows > 0;
  const rowH = rows <= 2 ? 14 : rows <= 4 ? 10 : 7;
  const trackH = twoLanes ? MAIN_H + 4 + rows * (rowH + 2) : 32;
  const scale = ZOOMS[zoom] ?? 1;
  // One label per ~110px so clock times never collide, whatever the width and zoom.
  const ticks = Math.max(1, Math.min(Math.floor((width * scale) / 110), 64));
  // Clock labels repeat when ticks are under a second apart; label offsets instead.
  const tickLabel = (t: number) =>
    total / ticks < 1000 ? `+${formatDuration(t * total)}` : formatTime(startAt + t * total);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => entry && setWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!selected) return;
    const el = scroller.current?.querySelector<HTMLElement>(
      `[data-event-id="${CSS.escape(selected)}"]`,
    );
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [selected, zoom]);

  const kinds = [...new Set(events.map((e) => e.kind))];

  return (
    <div className="grid gap-2">
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">{toolbar}</div>
        <div className="flex items-center" role="group" aria-label="Timeline zoom">
          <IconButton
            label="Zoom out"
            disabled={zoom === 0}
            onClick={() => setZoom((z) => Math.max(z - 1, 0))}
          >
            <ZoomOut />
          </IconButton>
          <span className="text-muted-foreground w-8 text-center text-xs tabular-nums">
            {scale}×
          </span>
          <IconButton
            label="Zoom in"
            disabled={zoom === ZOOMS.length - 1}
            onClick={() => setZoom((z) => Math.min(z + 1, ZOOMS.length - 1))}
          >
            <ZoomIn />
          </IconButton>
        </div>
      </div>
      <div
        ref={scroller}
        className="bg-muted/60 overflow-x-auto overflow-y-hidden rounded-lg border [scrollbar-width:thin]"
      >
        <div
          className="relative min-w-full px-1.5 pt-1.5"
          style={{ width: `${scale * 100}%` }}
          onPointerMove={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            setHover(Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1));
          }}
          onPointerLeave={() => setHover(null)}
          data-testid="event-timeline"
        >
          <div className="relative" style={{ height: trackH }}>
            {events.map((ev) => {
              const left = (ev.startMs / total) * 100;
              const end = ev.endMs ?? total;
              const width = Math.max(((end - ev.startMs) / total) * 100, 0);
              const isUser = ev.kind === 'user';
              const lane = LANE[ev.kind];
              const kind = ev.failed && ev.kind === 'agent' ? 'error' : ev.kind;
              return (
                <button
                  key={ev.id}
                  type="button"
                  data-event-id={ev.id}
                  aria-label={`${kindLabel(ev.kind)}: ${ev.label}, ${formatTime(startAt + ev.startMs)}${ev.endMs === null ? ', running' : isUser ? '' : `, ${formatDuration(end - ev.startMs)}`}`}
                  aria-pressed={selected === ev.id}
                  title={`${kindLabel(ev.kind)} · ${ev.label}`}
                  onClick={() => onSelect(ev.id)}
                  className={cn(
                    'focus-visible:ring-ring absolute rounded-[3px] transition-[background-color,box-shadow] duration-200 outline-none focus-visible:ring-2',
                    BLOCK[kind],
                    isUser ? 'z-10 w-0.5' : LANE[ev.kind] === 0 ? 'min-w-2.5' : 'min-w-1',
                    (isUser || !twoLanes) && 'inset-y-0',
                    ev.endMs === null && !isUser && 'animate-pulse',
                    selected === ev.id &&
                      'ring-foreground ring-offset-background ring-2 ring-offset-1',
                  )}
                  style={{
                    left: `${left}%`,
                    ...(isUser ? {} : { width: `${width}%` }),
                    ...(!isUser && twoLanes
                      ? lane === 0
                        ? { top: 0, height: MAIN_H }
                        : { top: MAIN_H + 4 + (rowOf.get(ev.id) ?? 0) * (rowH + 2), height: rowH }
                      : {}),
                  }}
                />
              );
            })}
            {hover !== null && (
              <span
                className="bg-foreground/70 pointer-events-none absolute -inset-y-1.5 z-20 w-px"
                style={{ left: `${hover * 100}%` }}
                aria-hidden
              />
            )}
          </div>
          <div className="text-muted-foreground relative h-6 text-[11px] tabular-nums" aria-hidden>
            {Array.from({ length: ticks + 1 }, (_, i) => i / ticks).map((t) => (
              <span
                key={t}
                className="absolute top-1 -translate-x-1/2 whitespace-nowrap first:translate-x-0 last:-translate-x-full"
                style={{ left: `${t * 100}%` }}
              >
                {tickLabel(t)}
              </span>
            ))}
            {hover !== null && (
              <span
                className="bg-popover text-popover-foreground absolute top-0.5 z-20 -translate-x-1/2 rounded border px-1.5 shadow-sm"
                style={{ left: `clamp(2.5rem, ${hover * 100}%, calc(100% - 2.5rem))` }}
              >
                {formatTime(startAt + hover * total)}
              </span>
            )}
          </div>
        </div>
      </div>
      {kinds.length > 1 && (
        <ul
          className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs"
          aria-label="Legend"
        >
          {kinds.map((k) => (
            <li key={k} className="inline-flex items-center gap-1.5">
              <span className={cn('size-2 rounded-[2px]', BLOCK[k])} aria-hidden />
              {kindLabel(k)}
              <span className="tabular-nums">{events.filter((e) => e.kind === k).length}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function IconButton({
  label,
  children,
  ...props
}: { label: string; children: ReactNode } & React.ComponentProps<'button'>) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className="text-muted-foreground hover:text-foreground hover:bg-accent focus-visible:ring-ring/50 inline-flex size-8 items-center justify-center rounded-md transition-colors outline-none focus-visible:ring-3 active:scale-95 disabled:pointer-events-none disabled:opacity-40 [&_svg]:size-4"
      {...props}
    >
      {children}
    </button>
  );
}

const GAP_MARKER_MS = 60_000;

/** "+12m" style label for an idle gap between two events. */
const gapLabel = (ms: number) =>
  ms >= 3_600_000
    ? `+${Math.round(ms / 3_600_000)}h`
    : ms >= 60_000
      ? `+${Math.round(ms / 60_000)}m`
      : `+${Math.round(ms / 1000)}s`;

/**
 * The session as a flow: one chip per event in order (user → agent → model → tools), so a short
 * conversation with long idle gaps stays readable. Idle gaps become "+12m" markers; click selects.
 */
export function FlowTimeline({
  events,
  startAt,
  selected,
  onSelect,
  toolbar,
  labels,
}: {
  events: TimelineEvent[];
  /** Epoch ms of offset 0. */
  startAt: number;
  totalMs?: number;
  selected: string | null;
  onSelect: (id: string) => void;
  /** Shown beside the flow (e.g. the transcript search). */
  toolbar?: ReactNode;
  /** Renames kinds in the legend and labels (e.g. the root lane of a workflow). */
  labels?: Partial<Record<TimelineKind, string>>;
}) {
  const kindLabel = (k: TimelineKind) => labels?.[k] ?? KIND_LABEL[k];
  const ordered = useMemo(() => [...events].sort((a, b) => a.startMs - b.startMs), [events]);
  const kinds = [...new Set(events.map((e) => e.kind))];
  const scroller = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!selected) return;
    scroller.current
      ?.querySelector<HTMLElement>(`[data-event-id="${CSS.escape(selected)}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selected]);

  return (
    <div className="grid min-w-0 gap-2">
      {toolbar && <div className="min-w-0">{toolbar}</div>}
      <div
        ref={scroller}
        className="bg-muted/60 max-h-40 overflow-y-auto rounded-lg border p-2.5 [scrollbar-width:thin]"
        data-testid="event-timeline"
      >
        <ol className="flex flex-wrap items-center gap-x-1 gap-y-1.5">
          {ordered.map((ev, i) => {
            const prev = ordered[i - 1];
            const gap = prev ? ev.startMs - (prev.endMs ?? prev.startMs) : 0;
            const kind = ev.failed && ev.kind === 'agent' ? 'error' : ev.kind;
            const isUser = ev.kind === 'user';
            const duration =
              ev.endMs === null ? 'running' : isUser ? null : formatDuration(ev.endMs - ev.startMs);
            const name = ev.kind === 'tool' || ev.kind === 'step' ? ev.label : kindLabel(ev.kind);
            return (
              <li key={ev.id} className="flex min-w-0 items-center gap-1">
                {i > 0 &&
                  (gap > GAP_MARKER_MS ? (
                    <span
                      className="text-muted-foreground border-border mx-1 rounded-full border border-dashed px-1.5 text-[11px] tabular-nums"
                      title={`${formatDuration(gap)} idle`}
                    >
                      {gapLabel(gap)}
                    </span>
                  ) : (
                    <ChevronRight
                      className="text-muted-foreground/60 size-3.5 shrink-0"
                      aria-hidden
                    />
                  ))}
                <button
                  type="button"
                  data-event-id={ev.id}
                  aria-label={`${kindLabel(ev.kind)}: ${ev.label}, ${formatTime(startAt + ev.startMs)}${duration ? `, ${duration}` : ''}`}
                  aria-pressed={selected === ev.id}
                  title={`${kindLabel(ev.kind)} · ${ev.label} · ${formatTime(startAt + ev.startMs)}`}
                  onClick={() => onSelect(ev.id)}
                  className={cn(
                    'bg-background hover:bg-accent focus-visible:ring-ring/50 inline-flex h-7 max-w-44 min-w-0 items-center gap-1.5 rounded-md border px-2 text-xs transition-colors outline-none focus-visible:ring-3',
                    selected === ev.id && 'border-foreground',
                    ev.endMs === null && 'animate-pulse',
                  )}
                >
                  <span className={cn('size-2 shrink-0 rounded-[2px]', BLOCK[kind])} aria-hidden />
                  <span className={cn('truncate', kind !== 'tool' && 'font-medium')}>{name}</span>
                  {duration && (
                    <span className="text-muted-foreground shrink-0 tabular-nums">{duration}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
      </div>
      {kinds.length > 1 && (
        <ul
          className="text-muted-foreground flex flex-wrap gap-x-4 gap-y-1 text-xs"
          aria-label="Legend"
        >
          {kinds.map((k) => (
            <li key={k} className="inline-flex items-center gap-1.5">
              <span className={cn('size-2 rounded-[2px]', BLOCK[k])} aria-hidden />
              {kindLabel(k)}
              <span className="tabular-nums">{events.filter((e) => e.kind === k).length}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The transcript search box. */
export function TranscriptSearch({
  value,
  onChange,
  matches,
}: {
  value: string;
  onChange: (value: string) => void;
  matches: number | null;
}) {
  return (
    <label className="bg-background focus-within:ring-ring/50 relative flex h-8 w-full max-w-xs items-center gap-2 rounded-md border px-2.5 transition-shadow focus-within:ring-3">
      <Search className="text-muted-foreground size-3.5 shrink-0" aria-hidden />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Find in transcript"
        aria-label="Find in transcript"
        className="placeholder:text-muted-foreground min-w-0 flex-1 bg-transparent text-sm outline-none"
      />
      {matches !== null && (
        <span className="text-muted-foreground shrink-0 text-xs tabular-nums">{matches}</span>
      )}
    </label>
  );
}

// --- Header ---

export function SessionTitle({ id, status }: { id: string; status: ReactNode }) {
  return (
    <div className="flex min-w-0 items-center gap-3">
      <h2
        className="min-w-0 truncate font-mono text-xl font-medium tracking-tight md:text-2xl"
        title={id}
      >
        {id}
      </h2>
      {status}
    </div>
  );
}

/** A status pill: quiet for idle/succeeded, pulsing for running, red for failures. */
export function StatusPill({ label, tone }: { label: string; tone: 'idle' | 'running' | 'error' }) {
  return (
    <span
      className={cn(
        'inline-flex h-6 shrink-0 items-center gap-1.5 rounded-md px-2 text-xs font-medium',
        tone === 'idle' && 'bg-accent text-foreground',
        tone === 'running' && 'bg-tint-blue/15 text-tint-blue',
        tone === 'error' && 'bg-destructive/10 text-destructive',
      )}
      data-testid="session-status"
    >
      {tone === 'running' && (
        <span className="bg-tint-blue size-1.5 animate-pulse rounded-full" aria-hidden />
      )}
      {label}
    </span>
  );
}

/** A linked (or plain) chip with an icon, truncated to a fixed width. */
export function SessionChip({
  icon: Icon,
  label,
  title,
  link,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: ReactNode;
  title?: string;
  link?: { to: string; params?: Record<string, string>; search?: Record<string, string> };
}) {
  const body = (
    <>
      <Icon className="text-muted-foreground size-3.5 shrink-0" aria-hidden />
      <span className="truncate">{label}</span>
    </>
  );
  const className =
    'inline-flex h-8 max-w-56 min-w-0 items-center gap-2 rounded-md border bg-surface px-2.5 text-sm transition-colors';
  return link ? (
    <Link
      // Chips link to app routes chosen by the page; the router checks them at runtime.
      to={link.to as '/'}
      params={link.params as never}
      search={link.search as never}
      title={title}
      className={cn(
        className,
        'hover:bg-accent focus-visible:ring-ring/50 outline-none focus-visible:ring-3',
      )}
    >
      {body}
    </Link>
  ) : (
    <span title={title} className={className}>
      {body}
    </span>
  );
}

export function SessionMetric({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm tabular-nums" title={label}>
      <Icon className="text-muted-foreground size-3.5 shrink-0" aria-hidden />
      <span className="sr-only">{label}: </span>
      {children}
    </span>
  );
}

// --- Transcript ---

/** A role tag above a transcript item: square, tinted by who spoke. */
export function RoleTag({
  role,
  children,
}: {
  role: 'user' | 'agent' | 'tool' | 'model' | 'error' | 'step';
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex h-5 w-fit max-w-64 items-center truncate rounded-[4px] px-1.5 text-xs font-medium',
        role === 'user' && 'bg-tint-rose/12 text-tint-rose',
        role === 'agent' && 'bg-tint-blue/12 text-tint-blue',
        role === 'model' && 'bg-tint-violet/12 text-tint-violet',
        role === 'tool' && 'bg-tint-amber/14 text-tint-amber',
        role === 'step' && 'bg-accent text-muted-foreground',
        role === 'error' && 'bg-destructive/10 text-destructive',
      )}
    >
      {children}
    </span>
  );
}

/** ↑ input ↓ output tokens, as a small tag pinned to a message's top edge. */
export function TokenTag({
  input,
  output,
  className,
}: {
  input: number | null;
  output: number | null;
  className?: string;
}) {
  if (input === null && output === null) return null;
  return (
    <span
      className={cn(
        'bg-background text-muted-foreground inline-flex h-5 items-center gap-2 rounded-full border px-2 font-mono text-[11px] tabular-nums',
        className,
      )}
      title={`${input ?? 0} input tokens · ${output ?? 0} output tokens`}
    >
      <span className="inline-flex items-center gap-0.5">
        <ArrowUpRight className="size-3" aria-hidden />
        {formatCompact(input ?? 0)}
      </span>
      <span className="inline-flex items-center gap-0.5">
        <ArrowDownRight className="size-3" aria-hidden />
        {formatCompact(output ?? 0)}
      </span>
    </span>
  );
}

/** One transcript entry: a tag line (role, time, extras) above its body. */
export function TranscriptItem({
  id,
  role,
  tag,
  at,
  extra,
  selected,
  dimmed,
  onSelect,
  children,
  testId,
}: {
  id: string;
  role: Parameters<typeof RoleTag>[0]['role'];
  tag: ReactNode;
  at: string | number;
  extra?: ReactNode;
  selected?: boolean;
  dimmed?: boolean;
  onSelect?: () => void;
  children?: ReactNode;
  testId?: string;
}) {
  return (
    <article
      id={`evt-${id}`}
      data-testid={testId}
      className={cn(
        'grid min-w-0 scroll-mt-4 gap-2 transition-opacity duration-200',
        dimmed && 'opacity-35',
      )}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-2.5 gap-y-1">
        <button
          type="button"
          onClick={onSelect}
          className="focus-visible:ring-ring/50 rounded-[4px] outline-none focus-visible:ring-3"
          aria-label={`Select ${typeof tag === 'string' ? tag : role} event`}
          aria-pressed={selected}
        >
          <RoleTag role={role}>{tag}</RoleTag>
        </button>
        <time className="text-muted-foreground text-xs whitespace-nowrap tabular-nums">
          {formatTime(at)}
        </time>
        {extra && (
          <div className="text-muted-foreground ml-auto flex flex-wrap items-center justify-end gap-2 text-xs whitespace-nowrap">
            {extra}
          </div>
        )}
      </div>
      {children && (
        <div
          className={cn(
            'min-w-0 overflow-hidden rounded-xl border transition-shadow duration-200',
            selected && 'ring-tint-blue/40 ring-2',
          )}
        >
          {children}
        </div>
      )}
    </article>
  );
}

/** The transcript's scroll area, with a "jump to latest" button while scrolled up. */
export function TranscriptScroll({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [away, setAway] = useState(false);
  const check = () => {
    const el = ref.current;
    if (el) setAway(el.scrollHeight - el.scrollTop - el.clientHeight > 120);
  };
  useEffect(check, [children]);
  return (
    <div className="relative mt-3 min-h-0 flex-1 border-t">
      <div
        ref={ref}
        onScroll={check}
        className="grid h-full grid-cols-[minmax(0,1fr)] content-start gap-6 overflow-x-hidden overflow-y-auto px-1 pt-5 pb-10 [scrollbar-width:thin] lg:pr-4"
      >
        {children}
      </div>
      {away && (
        <button
          type="button"
          aria-label="Scroll to latest"
          onClick={() =>
            ref.current?.scrollTo({ top: ref.current.scrollHeight, behavior: 'smooth' })
          }
          className="bg-popover hover:bg-accent animate-in fade-in absolute bottom-4 left-1/2 inline-flex size-9 -translate-x-1/2 items-center justify-center rounded-full border shadow-md transition-colors active:scale-95"
        >
          <ArrowDown className="size-4" aria-hidden />
        </button>
      )}
    </div>
  );
}

// --- Side panel ---

export function Facts({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-sm">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="min-w-0 truncate">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function PanelSection({
  title,
  aside,
  children,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="grid gap-3 border-t pt-5 first:border-t-0 first:pt-0">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-medium">{title}</h3>
        {aside && <span className="text-sm tabular-nums">{aside}</span>}
      </div>
      {children}
    </section>
  );
}

/** A cumulative step chart (tokens over time) with a dot on the latest value. */
export function CumulativeChart({
  points,
  startAt,
  endAt,
  format = formatCompact,
  label,
}: {
  /** Epoch ms and the value added at that moment. */
  points: Array<{ at: number; value: number }>;
  startAt: number;
  endAt: number;
  format?: (n: number) => string;
  label: string;
}) {
  const span = Math.max(endAt - startAt, 1);
  const sorted = [...points].sort((a, b) => a.at - b.at);
  let sum = 0;
  const steps = sorted.map((p) => ({ at: p.at, value: (sum += p.value) }));
  const max = Math.max(sum, 1);
  const x = (at: number) => ((at - startAt) / span) * 100;
  const y = (v: number) => 100 - (v / max) * 100;
  let d = `M 0 100`;
  let prev = 0;
  for (const s of steps) {
    d += ` H ${x(s.at)} V ${y(s.value)}`;
    prev = s.value;
  }
  d += ` H 100`;
  const area = `${d} V 100 Z`;
  const grid = [0, 1 / 3, 2 / 3, 1];
  if (sum === 0)
    return (
      <p className="text-muted-foreground bg-muted/50 rounded-lg px-3 py-6 text-center text-xs">
        No tokens recorded yet.
      </p>
    );
  return (
    <figure className="grid gap-1" aria-label={label}>
      <div className="grid grid-cols-[3rem_1fr] gap-2">
        <div className="text-muted-foreground relative h-32 text-[11px] tabular-nums">
          {grid.map((g) => (
            <span
              key={g}
              className="absolute right-0 -translate-y-1/2"
              style={{ top: `${(1 - g) * 100}%` }}
            >
              {format(Math.round(max * g))}
            </span>
          ))}
        </div>
        <div className="relative h-32">
          {grid.map((g) => (
            <span
              key={g}
              className="border-border absolute inset-x-0 border-t border-dashed"
              style={{ top: `${(1 - g) * 100}%` }}
              aria-hidden
            />
          ))}
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            className="absolute inset-0 size-full overflow-visible"
            aria-hidden
          >
            <path d={area} className="fill-tint-blue/10" />
            <path
              d={d}
              className="stroke-tint-blue fill-none"
              strokeWidth={1.75}
              vectorEffect="non-scaling-stroke"
              strokeLinejoin="round"
            />
          </svg>
          {steps.length > 0 && (
            <span
              className="bg-tint-blue ring-background absolute size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2"
              style={{ left: `${x(steps.at(-1)!.at)}%`, top: `${y(prev)}%` }}
              aria-hidden
            />
          )}
        </div>
      </div>
      <figcaption className="text-muted-foreground ml-[3.5rem] flex justify-between text-[11px] tabular-nums">
        <span>{formatTime(startAt)}</span>
        <span>{formatTime(endAt)}</span>
      </figcaption>
    </figure>
  );
}

/** Pretty JSON in a quiet, scrollable block. */
export function JsonBlock({ value, className }: { value: unknown; className?: string }) {
  return (
    <pre
      className={cn(
        'bg-muted/70 max-h-72 overflow-x-hidden overflow-y-auto rounded-lg p-3 font-mono text-xs leading-relaxed [overflow-wrap:anywhere] whitespace-pre-wrap [scrollbar-width:thin]',
        className,
      )}
    >
      {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
    </pre>
  );
}

const REQUEST_ROLE: Record<string, 'user' | 'agent' | 'step'> = {
  user: 'user',
  assistant: 'agent',
  system: 'step',
  tool: 'step',
};

/** The messages one model call was sent: system prompt, history, memory and tool results. */
export function RequestMessages({
  messages,
}: {
  messages: Array<{ role: string; content: string }>;
}) {
  return (
    <div className="grid min-w-0 gap-3">
      {messages.map((m, i) => (
        <div key={i} className="grid min-w-0 gap-1">
          <div className="flex items-center gap-2">
            <RoleTag role={REQUEST_ROLE[m.role] ?? 'step'}>{m.role}</RoleTag>
            <span className="text-muted-foreground text-xs tabular-nums">
              {m.content.length.toLocaleString()} chars
            </span>
          </div>
          <JsonBlock value={m.content} />
        </div>
      ))}
    </div>
  );
}
